// spriteIndex — build lookup indexes from a SpriteAtlas at load time.
//
// Indexes:
//   tileFullKeysByType  : Map<type, string[]>  — full tile sprites per terrain type
//   tileTransitionKeys  : Map<"type:X:Y", key> — transition overlay lookup
//   overlayKeysAll      : string[]             — all overlay sprite keys

/**
 * Build all indexes from a SpriteAtlas.
 * @param {import('./SpriteAtlas.mjs').SpriteAtlas} atlas
 * @returns {{ tileFullKeysByType: Map<string, string[]>,
 *             tileTransitionKeys: Map<string, string>,
 *             overlayKeysAll: string[] }}
 */
export function buildSpriteIndex(atlas) {
  /** @type {Map<string, string[]>} */
  const tileFullKeysByType = new Map();

  /** @type {Map<string, string>} */
  const tileTransitionKeys = new Map();

  /** @type {string[]} */
  const overlayKeysAll = [];

  for (const key of atlas.keys()) {
    if (key.startsWith('overlays/')) {
      overlayKeysAll.push(key);
      continue;
    }

    if (!key.startsWith('tiles/')) continue;

    // Extract type from path: tiles/<type>/...
    const parts = key.split('/');
    const type = parts[1]; // e.g. "ocean", "grass"

    if (key.includes('_tran_')) {
      // Transition sprite: e.g. tiles/ocean/transition/ocean_tran_2-0
      // Extract X-Y from suffix after _tran_
      const match = key.match(/_tran_(\d+)-(\d+)$/);
      if (match) {
        const x = match[1]; // minority count
        const y = match[2]; // start index
        // Key format: "type:X:Y"
        tileTransitionKeys.set(`${type}:${x}:${y}`, key);
      }
    } else {
      // Full tile sprite: e.g. tiles/ocean/ocean_tile_01
      if (!tileFullKeysByType.has(type)) tileFullKeysByType.set(type, []);
      tileFullKeysByType.get(type).push(key);
    }
  }

  return { tileFullKeysByType, tileTransitionKeys, overlayKeysAll };
}

/** @param {string} key */
export function isWaterTile(key) {
  return key.includes('ocean');
}

/** @param {string} key */
export function isWaterOverlay(key) {
  return key.includes('water') || key.includes('ship');
}

// ===== OVERLAY ECONOMY =====

/** Resolve an overlay sprite key to its economy family. */
export function getOverlayFamily(overlayKey) {
  if (overlayKey.startsWith('overlays/animal/land/'))    return 'animal_land';
  if (overlayKey.startsWith('overlays/animal/water/'))   return 'animal_water';
  if (overlayKey.startsWith('overlays/vegetation/'))     return 'vegetation';
  if (overlayKey.startsWith('overlays/forest/'))         return 'forest';
  if (overlayKey.startsWith('overlays/mountainrocks/'))  return 'mountainrocks';
  if (overlayKey.startsWith('overlays/island/water/'))   return 'island_water';
  if (overlayKey.startsWith('overlays/island/land/'))    return 'island_land';
  if (overlayKey.startsWith('overlays/structure/'))      return 'structure';
  if (overlayKey.startsWith('overlays/ship/'))           return 'ship';
  if (overlayKey.startsWith('overlays/misc/')) {
    if (overlayKey.includes('/campsite_'))       return 'campsite';
    if (overlayKey.endsWith('/castle_ruin'))     return 'castle_ruin';
    if (overlayKey.includes('/dragon_'))         return 'dragon';
    if (overlayKey.includes('/geysers_'))        return 'geysers';
    if (overlayKey.endsWith('/flying_castle'))   return 'flying_castle';
    if (overlayKey.includes('/graveyard_'))      return 'graveyard';
  }
  return 'vegetation'; // safe fallback
}

/** Family → { resource, bonusTerrains } mapping per spec §3. */
export const OVERLAY_ECONOMY = {
  animal_land:   { resource: 'food',     bonusTerrains: ['grass','grassland','snow'] },
  animal_water:  { resource: 'fish',     bonusTerrains: ['ocean'] },
  vegetation:    { resource: 'food',     bonusTerrains: ['grass','grassland'] },
  forest:        { resource: 'wood',     bonusTerrains: ['grass','grassland','snow'] },
  mountainrocks: { resource: 'stone',    bonusTerrains: ['mountain','deadplains'] },
  island_water:  { resource: 'food',     bonusTerrains: ['ocean'] },
  island_land:   { resource: 'food',     bonusTerrains: ['desert','grassland'] },
  structure:     { resource: 'supplies', bonusTerrains: ['grass','grassland','desert'] },
  ship:          { resource: 'fish',     bonusTerrains: ['ocean'] },
  campsite:      { resource: 'supplies', bonusTerrains: ['grass','grassland'] },
  castle_ruin:   { resource: 'gold',     bonusTerrains: ['mountain','deadplains'] },
  dragon:        { resource: 'crystals', bonusTerrains: ['mountain','desert'] },
  geysers:       { resource: 'crystals', bonusTerrains: ['mountain','snow'] },
  flying_castle: { resource: 'crystals', bonusTerrains: ['mountain','snow'] },
  graveyard:     { resource: 'crystals', bonusTerrains: ['deadplains','snow'] },
};

/**
 * Compute the reward for placing an overlay.
 * @param {string} overlayKey - Atlas sprite key
 * @param {string} terrainType - Majority terrain of the target tile
 * @param {number} neighbourOverlayCount - Adjacent hexes with overlays (0–6)
 * @returns {{ stars: number, resources: Record<string, number> }}
 */
export function computeOverlayPlacementReward(overlayKey, terrainType, neighbourOverlayCount) {
  const family = getOverlayFamily(overlayKey);
  const econ = OVERLAY_ECONOMY[family];
  const terrainBonus = econ.bonusTerrains.includes(terrainType) ? 1 : 0;
  const stars = 1 + neighbourOverlayCount;
  const amount = 1 + terrainBonus + neighbourOverlayCount;
  return { stars, resources: { [econ.resource]: amount } };
}
